import { z } from 'zod';
import { analystLevelSchema, analystPassSchema } from './analyst.js';
import {
  NOYAU_CODES,
  NOYAU_MAX_GAP_DEFAULT,
  noyauBlockModeSchema,
  noyauChoiceModeSchema,
  noyauCodeSchema,
  noyauMaxGapSchema,
} from './noyau.js';
import { protocolLocalIdSchema } from './protocol.js';
import {
  VERSIONED_DEFAULT_SORT,
  renameRequestSchemaFor,
  saveDraftRequestSchema,
  saveVersionRequestSchema,
  versionPinSchema,
  versionedCardShape,
  versionedContentSchema,
  versionedDetailSchema,
  versionedFiltersShape,
  versionedRefSchema,
  versionedSortSchema,
} from './versioning.js';

/**
 * An experience: the LINE OF PAGES a person walks through, and the theme that
 * paints them (refonte modulaire §5.4, §9, §10.2, §15; grill Expériences of
 * 2026-09-10 and the three review passes of 2026-09-11, issue #51).
 *
 * Fourth consumer of the common versioning pattern (§4), and the one that cites
 * the most: a protocol and a persona by `{ id, pin }` reference, and files of
 * the media library by opaque id. Everything generic - the versions, the two
 * pointers, the draft, the retirement, « utilisé dans » - comes from
 * `versioning.ts`; what is here is only what depends on what an experience
 * MEANS.
 *
 * ## The stored schema is PERMISSIVE, and the refusals are elsewhere
 *
 * §9.1 says what « Enregistrer une nouvelle version » refuses: a protocol
 * chosen without a persona, a variable that does not resolve, a file of the
 * library that is gone, two variables of the same name. None of those is a
 * shape a Zod object can refuse - the persona is legitimately null while
 * somebody picks one, a variable token is a cross-reference inside the same
 * content, and whether a file still exists is a question only the base can
 * answer. So the schema accepts them all, and ONE pure rule set -
 * `experienceProblems` in `../experience/problems.ts` - names each of them, for
 * the server that refuses the version and for the editor that greys the box.
 *
 * What the schema DOES hold are the two structural invariants of §9.1: exactly
 * one protocol page, and a first temps that starts with the page. The editor
 * maintains both on every gesture, so a stored row can only violate them if a
 * client wrote it by hand - which is exactly what a wire-level refinement is
 * for. Same arrangement as `actionsWellOrdered` on a protocol's conditions.
 *
 * ## Identifiers are English, labels are French
 *
 * `next-temps`, `slide-left`, `bullet-list`: a stored enum is code, and the
 * French sentence beside each of them lives in the frontend's own catalogue.
 * The one French thing stored is what the person reads - a button's label, a
 * paragraph's words - and that is the team's words, not an identifier.
 *
 * `temps` itself keeps its French name, and deliberately: it is the team's word
 * for the thing §9.3 describes (« un temps groupe ce qui apparaît ensemble »),
 * and no English noun - beat, step, stage, phase - says it without colliding
 * with a protocol's `step` or with a session's `turn`.
 *
 * ## Page, temps and block ids are the CLIENT's
 *
 * A variable stores `{{champ:<blockId>}}` or `{{choix:<pageId>}}` (§8.6), so an
 * id is an ADDRESS and the « nom de variable » of the inspector is only a
 * label: renaming a variable is free, and nothing has to be rewritten. The
 * editor strikes the ids (a uuid) when it adds a row; the server checks they
 * are unique inside one content and nothing more.
 */

/* --------------------------------------------------------------- the bounds */

/**
 * H23 (§16). Bounds of INPUT, not of meaning: an operator meets them by
 * mistake, never on purpose.
 */
export const EXPERIENCE_NAME_MAX = 80;
export const EXPERIENCE_DESCRIPTION_MAX = 300;
export const EXPERIENCE_PAGE_NAME_MAX = 60;
export const EXPERIENCE_PAGES_MAX = 30;
export const EXPERIENCE_BLOCKS_MAX = 20;
export const EXPERIENCE_TEMPS_MAX = 10;
export const EXPERIENCE_TEXT_MAX = 4000;
export const EXPERIENCE_BUTTON_LABEL_MAX = 60;
export const EXPERIENCE_INPUT_LABEL_MAX = 200;
export const EXPERIENCE_CHECKBOX_LABEL_MAX = 600;
/** The question of a Sélection and of a Note (§4.1, §4.2 of retour-experience.md, issue #176). */
export const EXPERIENCE_SELECTION_QUESTION_MAX = 200;
export const EXPERIENCE_NOTE_QUESTION_MAX = 200;
/** One option of a Sélection, and how many a Sélection holds (§4.1: 2 to 5, 40 characters each). */
export const EXPERIENCE_SELECTION_OPTION_MAX = 40;
export const EXPERIENCE_SELECTION_OPTIONS_MIN = 2;
export const EXPERIENCE_SELECTION_OPTIONS_MAX = 5;
/** The scale of a Note: five stars, fixed, nothing to set (§4.2). */
export const EXPERIENCE_NOTE_STARS = 5;
/** The phrase of thanks of the feedback page (§2), plain text and never empty on that page. */
export const EXPERIENCE_THANKS_MAX = 600;
export const EXPERIENCE_VARIABLE_NAME_MAX = 40;
/** The address of a Lien (§9.2, issue #137), bounded like a long URL and never like a text. */
export const EXPERIENCE_LINK_URL_MAX = 2000;
export const EXPERIENCE_CAPTION_MAX = 200;
export const EXPERIENCE_OVERLAY_MAX = 2000;
/** The Contexte of the protocol page (§9.5), bounded like the overlay it follows (grill Variables, D2). */
export const EXPERIENCE_CONTEXT_MAX = 2000;
/** The delay before a temps, in seconds - a fraction is allowed (« délai 1,5 s »). */
export const EXPERIENCE_TEMPS_DELAY_MAX = 30;
/** A page's optional « durée automatique », in seconds (§9.2, the intertitre). */
export const EXPERIENCE_AUTO_DURATION_MIN = 1;
export const EXPERIENCE_AUTO_DURATION_MAX = 600;
/** The display conditions one block may carry (§9.2, H67). A bound of input, like H23's. */
export const EXPERIENCE_BLOCK_CONDITIONS_MAX = 10;

/**
 * The id of a page, a temps or a block: struck by the editor, stable across
 * reorders and renames, and the ADDRESS a variable token carries (§8.6).
 */
export const experienceLocalIdSchema = z.string().min(1).max(64);

/* ---------------------------------------------------------------- the theme */

/**
 * The five self-hosted families of §9.4 (H24), by their stable identifier. The
 * display name (« Playfair Display ») is the screen's, the identifier is the
 * one the reader loads a woff2 by - and it is loaded ONLY when a theme names
 * it, because five families in three weights are fifteen files.
 */
export const experienceFontSchema = z.enum([
  'titillium-web',
  'source-sans-3',
  'lora',
  'playfair-display',
  'caveat',
]);
export type ExperienceFont = z.infer<typeof experienceFontSchema>;

/** The size scale of the typography group, and the one a block may override. */
export const experienceScaleSchema = z.enum(['small', 'normal', 'large']);
export type ExperienceScale = z.infer<typeof experienceScaleSchema>;

/**
 * The RAPIDITÉ of §9.3, three levels and never a free duration (H26).
 *
 * Carried by a TEMPS for its blocks, and by an edge for its page transition. A
 * block never forces its own: it plays at the speed of its temps.
 */
export const animationSpeedSchema = z.enum(['fast', 'normal', 'slow']);
export type AnimationSpeed = z.infer<typeof animationSpeedSchema>;

/**
 * What the three words are worth in milliseconds (H26, §16; the mockup's
 * `ModeAnimation.dc.html` poses them).
 *
 * Exported from `shared` because THREE readers play them: the editor's preview,
 * the reader, and whatever asserts on either. Three words for the operator,
 * three durations for the reader, one table for the repository.
 */
export const ANIMATION_SPEED_MS = {
  fast: 150,
  normal: 300,
  slow: 600,
} as const satisfies Record<AnimationSpeed, number>;

/** The eight entry animations of §9.3, chosen by icon and the same everywhere. */
export const blockAnimationSchema = z.enum([
  'none',
  'fade',
  'wipe',
  'zoom',
  'slide-left',
  'slide-right',
  'slide-up',
  'slide-down',
]);
export type BlockAnimation = z.infer<typeof blockAnimationSchema>;

/** The transition between two pages (§9.3): three, not eight - it moves a whole page. */
export const pageTransitionAnimationSchema = z.enum(['none', 'fade', 'slide']);
export type PageTransitionAnimation = z.infer<typeof pageTransitionAnimationSchema>;

export const buttonShapeSchema = z.enum(['square', 'rounded', 'pill']);
export type ButtonShape = z.infer<typeof buttonShapeSchema>;

export const buttonStyleSchema = z.enum(['outline', 'solid']);
export type ButtonStyle = z.infer<typeof buttonStyleSchema>;

/** Where the background comes from (§9.4): a flat colour, or a file of the library. */
export const themeBackgroundKindSchema = z.enum(['color', 'image']);
export type ThemeBackgroundKind = z.infer<typeof themeBackgroundKindSchema>;

/**
 * A colour, `#rrggbb` and nothing else (H23).
 *
 * Six digits and no alpha: the one place transparency is a control is the
 * background veil, which has its own token, and two ways of writing « a bit
 * see-through » would be two ways of reading it back.
 */
export const themeColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/** The block-level tokens of §9.2: how a block sits in the vertical flow. */
export const blockAlignSchema = z.enum(['left', 'center', 'right']);
export type BlockAlign = z.infer<typeof blockAlignSchema>;

export const blockWidthSchema = z.enum(['full', 'content']);
export type BlockWidth = z.infer<typeof blockWidthSchema>;

export const blockSpacingSchema = z.enum(['tight', 'normal', 'loose']);
export type BlockSpacing = z.infer<typeof blockSpacingSchema>;

/**
 * The colour a passage of rich text may take (H28): one of the theme's own,
 * never a free colour - so the theme stays coherent and the contrast stays
 * calculable.
 */
export const textColorTokenSchema = z.enum(['text', 'muted', 'accent']);
export type TextColorToken = z.infer<typeof textColorTokenSchema>;

/**
 * The theme as an object of tokens to a KNOWN LIST (§9.4). No free CSS, at any
 * level.
 *
 * Every token is optional, at EVERY level, and that is the whole of « trois
 * niveaux de surcharge, tous partiels » : the same shape carries the
 * experience's theme and a page's overrides, and `resolveTheme` merges them
 * over the PMS default. A flat object rather than five nested groups, because
 * the grouping is a fact of the bar's layout (typographie, couleurs, fond,
 * boutons, mouvement) and a merge of nested partials is a merge nobody reads
 * twice the same way.
 *
 * NOT `.strict()`, like every stored shape (§4.1): a token added later must not
 * make today's rows unreadable.
 */
export const experienceThemeSchema = z.object({
  /* typographie */
  titleFont: experienceFontSchema.optional(),
  textFont: experienceFontSchema.optional(),
  textScale: experienceScaleSchema.optional(),
  /* couleurs */
  background: themeColorSchema.optional(),
  text: themeColorSchema.optional(),
  muted: themeColorSchema.optional(),
  accent: themeColorSchema.optional(),
  buttonBackground: themeColorSchema.optional(),
  buttonText: themeColorSchema.optional(),
  aiBubble: themeColorSchema.optional(),
  personBubble: themeColorSchema.optional(),
  /* fond */
  backgroundKind: themeBackgroundKindSchema.optional(),
  /** A file of the media library, by opaque id. Null when the fond is a colour. */
  backgroundImageAssetId: z.string().min(1).nullable().optional(),
  /** The veil that darkens an image so the text stays readable, 0 to 1 (H23). */
  backgroundVeil: z.number().min(0).max(1).optional(),
  /* boutons */
  buttonShape: buttonShapeSchema.optional(),
  buttonSize: experienceScaleSchema.optional(),
  buttonStyle: buttonStyleSchema.optional(),
  /* mouvement */
  blockAnimation: blockAnimationSchema.optional(),
  blockSpeed: animationSpeedSchema.optional(),
  pageTransition: pageTransitionAnimationSchema.optional(),
  pageTransitionSpeed: animationSpeedSchema.optional(),
  /** Respects the person's own system setting; the delays between temps stay (§9.3). */
  reduceMotion: z.boolean().optional(),
});
export type ExperienceTheme = z.infer<typeof experienceThemeSchema>;

/**
 * Every token resolved - what the reader and the preview paint with.
 *
 * Derived from `ExperienceTheme` rather than hand-written: a token added above
 * is a token this type demands, and `DEFAULT_EXPERIENCE_THEME` below stops
 * compiling until it has a default. That is the check that keeps the merge
 * total.
 *
 * `Exclude<..., undefined>` and not a bare `Required<>`: under
 * `exactOptionalPropertyTypes`, Zod infers `.optional()` as an explicit
 * `| undefined`, which the `-?` of `Required<>` leaves in place - so the
 * "resolved" theme would still hand every reader a possibly-undefined token,
 * and the totality this type exists to promise would be a comment.
 */
export type ResolvedExperienceTheme = {
  [Token in keyof ExperienceTheme]-?: Exclude<ExperienceTheme[Token], undefined>;
};

/**
 * The PMS default of §9.4 - what a new experience is born with, and what an
 * absent token falls back to at every level.
 *
 * Titillium Web on both faces is the admin's own type; the colours are the
 * neutral reading pair rather than the warm example of the mockup (« Accueil
 * PMS » was drawn to show that the reader does not look like the admin, and it
 * is an example, not a default).
 */
export const DEFAULT_EXPERIENCE_THEME: ResolvedExperienceTheme = {
  titleFont: 'titillium-web',
  textFont: 'titillium-web',
  textScale: 'normal',
  background: '#ffffff',
  text: '#1f2430',
  muted: '#6b7280',
  accent: '#312783',
  buttonBackground: '#312783',
  buttonText: '#ffffff',
  aiBubble: '#f3f4f6',
  personBubble: '#e8e6f5',
  backgroundKind: 'color',
  backgroundImageAssetId: null,
  backgroundVeil: 0,
  buttonShape: 'rounded',
  buttonSize: 'normal',
  buttonStyle: 'solid',
  blockAnimation: 'fade',
  blockSpeed: 'normal',
  pageTransition: 'fade',
  pageTransitionSpeed: 'normal',
  reduceMotion: true,
};

/**
 * The three tokens a BLOCK may override (§9.4: « alignement, taille, couleur du
 * texte seulement »), plus the two §9.2 gives every block - its width and its
 * spacing in the vertical flow.
 *
 * Null is « hérité » and is the only spelling of it: an absent key and a null
 * would be two ways of saying the same nothing, and every reader of a draft
 * would have to know about both.
 *
 * An image's « largeur » and « alignement » are THESE - the inspector surfaces
 * them in its Contenu panel as well (the mockup says so in as many words:
 * « L'alignement est celui du bloc, aussi dans l'onglet Visuel »), and one
 * field shown twice is not two fields stored twice.
 */
export const blockVisualSchema = z.object({
  align: blockAlignSchema.nullable().default(null),
  width: blockWidthSchema.nullable().default(null),
  spacing: blockSpacingSchema.nullable().default(null),
  textScale: experienceScaleSchema.nullable().default(null),
  textColor: textColorTokenSchema.nullable().default(null),
});
export type BlockVisual = z.infer<typeof blockVisualSchema>;

export const EMPTY_BLOCK_VISUAL: BlockVisual = {
  align: null,
  width: null,
  spacing: null,
  textScale: null,
  textColor: null,
};

/* ------------------------------------------------------------ the rich text */

/**
 * The closed list of §9.2 - and H28/H29 are TRANCHÉES here, in favour of a
 * small JSON tree.
 *
 * H29 offered « Markdown étendu ou petit arbre JSON ». Extended Markdown is
 * out, and for a reason that is not taste: §9.2 asks for **souligné** and for a
 * **colour among the theme's**, and Markdown has a syntax for neither. Writing
 * one would mean inventing `++souligné++` and `{accent|mot}`, i.e. a dialect
 * whose only parser is ours - and a text stored in a dialect is a text nobody
 * can read without our code. The tree below says exactly what exists and
 * nothing more, and a node the parser does not know is a version this schema
 * refuses rather than a paragraph that silently loses its colour.
 *
 * H28 is tranchée the same way: the colour of a passage is one of `text`,
 * `muted`, `accent` - a TOKEN and never a `#rrggbb` - so a change of theme
 * repaints the text and the contrast of §9.4 stays calculable.
 *
 * What is NOT here, and deliberately: titles, links, tables, and any HTML,
 * which is escaped. The renderer is `renderRichText`, pure and shared by the
 * preview and the reader, so there is never two spellings of one text.
 */
export const richTextMarkSchema = z.enum(['bold', 'italic', 'underline']);
export type RichTextMark = z.infer<typeof richTextMarkSchema>;

/**
 * One inline node. A `text` run carries its marks and its colour; a `break` is
 * the « retour à la ligne » of §9.2, inside one paragraph.
 *
 * A variable is NOT a node: it stays a TOKEN inside the run's text
 * (`{{champ:<blockId>}}`, §8.6), the very spelling a protocol's directions use.
 * One spelling of a variable across the repository is what keeps « what the
 * editor inserted » and « what the server counts » from drifting - and it is
 * what lets `experienceVariablesOf` read a whole page without walking a tree.
 */
export const richTextInlineSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('text'),
    text: z.string(),
    marks: z.array(richTextMarkSchema).max(3).default([]),
    color: textColorTokenSchema.nullable().default(null),
  }),
  z.object({ type: z.literal('break') }),
]);
export type RichTextInline = z.infer<typeof richTextInlineSchema>;

/** One block-level node: a paragraph, or a bullet list whose items are inline runs. */
export const richTextNodeSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('paragraph'),
    children: z.array(richTextInlineSchema).default([]),
  }),
  z.object({
    type: z.literal('bullet-list'),
    items: z.array(z.object({ children: z.array(richTextInlineSchema).default([]) })).default([]),
  }),
]);
export type RichTextNode = z.infer<typeof richTextNodeSchema>;

/**
 * A rich text document: the whole content of one Texte block.
 *
 * An object around the list rather than a bare array, so a document-level
 * field added later does not change the shape of every stored text (§4.1).
 */
export const richTextSchema = z.object({
  nodes: z.array(richTextNodeSchema).default([]),
});
export type RichText = z.infer<typeof richTextSchema>;

export const EMPTY_RICH_TEXT: RichText = { nodes: [] };

/**
 * The words of a document, without its marks - what the 4000-character bound of
 * H23 is measured on, and what the inspector counts under the WYSIWYG zone.
 *
 * A line break and a list item each count as ONE character, so an operator who
 * splits a paragraph does not see the counter jump; the tokens of a variable
 * count as written, because that is what the base stores.
 */
export function richTextPlain(document: RichText): string {
  const run = (children: readonly RichTextInline[]): string =>
    children.map((inline) => (inline.type === 'break' ? '\n' : inline.text)).join('');
  return document.nodes
    .map((node) =>
      node.type === 'paragraph'
        ? run(node.children)
        : node.items.map((i) => run(i.children)).join('\n'),
    )
    .join('\n');
}

/* --------------------------------------------------------------- the blocks */

export const blockTypeSchema = z.enum([
  'text',
  'image',
  'sound',
  'button',
  'checkbox',
  'input',
  'link',
  'copyable',
  'noyau',
  'selection',
  'note',
]);
export type BlockType = z.infer<typeof blockTypeSchema>;

/**
 * What a Bouton does at the click (§9.2, H30) - and, since issue #176, `submit`:
 * « Envoyer le retour » (retour-experience.md §5), offered on the feedback page
 * ONLY. That is a rule and not a narrower shape (`submit-outside-feedback`).
 */
export const buttonActionSchema = z.enum(['next-page', 'next-temps', 'submit']);
export type ButtonAction = z.infer<typeof buttonActionSchema>;

/** An Input on one line is checked for its format - at the browser AND at the server (§9.2). */
export const inputFormatSchema = z.enum(['text', 'email']);
export type InputFormat = z.infer<typeof inputFormatSchema>;

export const inputLinesSchema = z.enum(['single', 'multi']);
export type InputLines = z.infer<typeof inputLinesSchema>;

/* ----------------------------------------------------- the address of a Lien */

/**
 * The two schemes a Lien may carry (§9.2, issue #137), and nothing else.
 *
 * `https:` and `mailto:` are the two ways out of an experience that cost the
 * person nothing she did not ask for. Every other scheme is refused BY NAME
 * below, because « adresse invalide » on a `javascript:` is the one sentence
 * that teaches an operator nothing: she typed something that works elsewhere,
 * and she needs to be told THAT it is this field which refuses it, and why.
 */
export const LINK_URL_SCHEMES = ['https:', 'mailto:'] as const;

/**
 * Why an address is refused, in the operator's own language (§9.2).
 *
 * A NAMED refusal and never a silent `false`: this table is what the inspector
 * writes under the field, what the version save returns in its 409, and what
 * the MCP `check_experience` relays. One sentence per cause, one source.
 */
export const LINK_URL_REFUSAL = {
  /** A `{{champ:...}}` in a query string would hand a personal answer to a third party at every click. */
  variable:
    'Une adresse ne porte aucune variable : un jeton dans une adresse enverrait une réponse de la personne à un tiers à chaque clic.',
  javascript:
    'Une adresse en « javascript: » est refusée : ce n’est pas un lien, c’est du code joué dans la page.',
  data: 'Une adresse en « data: » est refusée : elle embarque son propre contenu, que personne n’a relu.',
  http: 'Une adresse en « http: » est refusée : la liaison n’est pas chiffrée. Écris « https: ».',
  scheme: 'Une adresse commence par « https:// » ou par « mailto: », et par rien d’autre.',
  shape: 'Cette adresse n’est pas lisible : écris-la en entier, « https://… » ou « mailto:… ».',
} as const;

export type LinkUrlRefusal = (typeof LINK_URL_REFUSAL)[keyof typeof LINK_URL_REFUSAL];

/**
 * What refuses this address, or null when nothing does (§9.2, issue #137).
 *
 * PURE and in `shared` for the reason every other rule of this file is: the
 * inspector greys its field with it, the schema below refuses the version with
 * it, and the reader refuses to BUILD an `href` out of an address it did not
 * pass. Three consumers, one rule - two spellings would be a link the editor
 * accepted and the session would not open.
 *
 * The order matters, and it is the order of what the operator most needs to
 * hear: the variable first, because it is the only refusal that is about the
 * PERSON rather than about the address.
 */
export function linkUrlRefusal(url: string): LinkUrlRefusal | null {
  const address = url.trim();
  if (address.includes('{{')) return LINK_URL_REFUSAL.variable;

  const lower = address.toLowerCase();
  if (lower.startsWith('javascript:')) return LINK_URL_REFUSAL.javascript;
  if (lower.startsWith('data:')) return LINK_URL_REFUSAL.data;
  if (lower.startsWith('http:')) return LINK_URL_REFUSAL.http;

  /* `URL` and not a regular expression: a scheme is what a browser reads as a
   * scheme, and a hand-rolled pattern is how `https:/evil` gets through. A
   * throw here means the address is not absolute, which is its own refusal -
   * a relative address inside an experience leads nowhere. */
  let parsed: URL;
  try {
    parsed = new URL(address);
  } catch {
    return LINK_URL_REFUSAL.shape;
  }
  const scheme: string = parsed.protocol;
  if (!LINK_URL_SCHEMES.some((allowed) => allowed === scheme)) return LINK_URL_REFUSAL.scheme;
  return null;
}

/**
 * The address a VERSION stores: non empty, bounded, and refused by name.
 *
 * The refusal sits on the FIELD and not on the member: a `.superRefine` on a
 * member of a `discriminatedUnion` would make it a `ZodEffects` the union
 * refuses to build - the same constraint `refuseOversizedText` states below.
 */
export const experienceLinkUrlSchema = z
  .string()
  .trim()
  .min(1)
  .max(EXPERIENCE_LINK_URL_MAX)
  .superRefine((url, ctx) => {
    const refusal = linkUrlRefusal(url);
    if (refusal === null) return;
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: refusal });
  });

/**
 * The address a DRAFT holds, and why it refuses LESS than the version.
 *
 * It refuses the variable - a token is INSERTED, never half typed, so refusing
 * it costs no keystroke - and it lets the scheme alone. A draft is autosaved at
 * the keystroke, and `h`, `ht`, `htt`, `http`, `http:` is the road to
 * `https://`: a schema that refused the fifth character would answer the
 * autosave with a 400 and lose the page somebody is composing. The VERSION
 * refuses, the inspector says so under the field as it is typed, and the reader
 * builds no `href` it did not pass - so nothing dangerous is ever rendered or
 * stored in production.
 */
export const experienceLinkUrlDraftSchema = z
  .string()
  .trim()
  .max(EXPERIENCE_LINK_URL_MAX)
  .default('')
  .superRefine((url, ctx) => {
    if (!url.includes('{{')) return;
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: LINK_URL_REFUSAL.variable });
  });

/* ----------------------------------------- what a feedback text may not carry */

/**
 * The sentence under a field that refuses a token (retour-experience.md §2,
 * §4.1): the option of a Sélection and the phrase of thanks are PLAIN text, and
 * a `{{champ:...}}` typed by hand would reach the person raw - the one thing
 * that must never happen (§8.6).
 */
export const FEEDBACK_NO_VARIABLE_REFUSAL =
  'Ce texte ne porte aucune variable : la personne en verrait le jeton brut.';

/**
 * The refusal sits on the FIELD and not on the member of the union, for the
 * reason `experienceLinkUrlSchema` states: a `.superRefine` on a member of a
 * `discriminatedUnion` would make it a `ZodEffects` the union refuses to build.
 */
function refuseVariableToken(text: string, ctx: z.RefinementCtx): void {
  if (!text.includes('{{')) return;
  ctx.addIssue({ code: z.ZodIssueCode.custom, message: FEEDBACK_NO_VARIABLE_REFUSAL });
}

/** One option of a Sélection, bounded by the schema; its emptiness and its twin are `selection-options`'s. */
const selectionOptionSchema = z
  .string()
  .trim()
  .max(EXPERIENCE_SELECTION_OPTION_MAX)
  .superRefine(refuseVariableToken);

/**
 * The options of a Sélection (§4.1).
 *
 * The COUNT, the empty option and the twin are NOT the schema's: the named
 * refusal `selection-options` of `experienceProblems` says each of them, and a
 * bound refused here would be a bare 400 the operator cannot read. The one
 * cap kept is a bound of input, far above the five a Sélection admits.
 */
const selectionOptionsSchema = z.array(selectionOptionSchema).max(EXPERIENCE_BLOCKS_MAX);

/**
 * The phrase of thanks a feedback page carries when its content says none
 * (retour-experience.md §3, word for word): the template's, and what an absent
 * `thanks` reads as (`feedbackThanksOf`).
 */
export const DEFAULT_FEEDBACK_THANKS =
  'Merci, votre retour est bien enregistré. Vous pouvez fermer cette page.';

/** The phrase of thanks (§2): plain text, bounded here, never empty on a feedback page - a rule. */
const thanksSchema = z
  .string()
  .trim()
  .max(EXPERIENCE_THANKS_MAX)
  .superRefine(refuseVariableToken)
  .optional();

/* --------------------------------------------------- the display conditions */

/**
 * ONE display condition of a block (§9.2 of the refonte, revue du 2026-09-25;
 * noyau.md §3.6): a variable in reach, and the operator of its NATURE.
 *
 * A block carries a LIST of them, ALL true for it to show; the empty list, the
 * default, is « toujours affiché ». The « ou » lives INSIDE a condition, by its
 * values (« SGP, SPG » for the Souverain family), the « et » BETWEEN conditions.
 *
 * ## One member per NATURE, so the operator is closed by the schema (H67)
 *
 * | Nature | Operator | Values |
 * |---|---|---|
 * | `noyau` - the retained noyau | `is-one-of` | one or more of the six triplets |
 * | `choice` - the choice of a page | `is-one-of` | one or more BUTTONS of that page |
 * | `checkbox` - a `{{champ:...}}` of a Checkbox | `checked`, `unchecked` | none |
 * | `input` - a `{{champ:...}}` of an Input | `filled`, `empty` | none - the text is never compared (Q25) |
 * | `objective` - after the protocol page only | `succeeded`, `not-succeeded` | none - « réussi » counts the passable (H68) |
 *
 * A CRITERION has no member and cannot be conditioned: it is a direction, not
 * a state (H68). A Checkbox and an Input are two members although both are a
 * `{{champ:...}}`, because their operators differ: a condition that names a
 * Checkbox by an Input's id does not resolve, which is how a nature and its
 * block are kept in step without a sixth refusal.
 *
 * ## A choice names its buttons by ID, never by label
 *
 * The session retains the LABEL pressed (§8.6), and the operator picks among
 * the labels; what is stored is the id of each button, the doctrine of every
 * reference of this repository (« le jeton porte l'identifiant, jamais le
 * nom »). A button relabelled since is still the button the condition meant, and
 * `displayConditionHolds` reads its label off the same frozen content the
 * session played.
 *
 * Not `.strict()`, like every stored shape (§4.1). The evaluation is pure and
 * lives in `experience/conditions.ts`; the refusals - reach, resolution, and a
 * condition on the way out of a page - live in `experience/problems.ts`.
 */
export const displayConditionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('noyau'),
    operator: z.literal('is-one-of'),
    noyaux: z.array(noyauCodeSchema).min(1).max(NOYAU_CODES.length),
  }),
  z.object({
    kind: z.literal('choice'),
    /** The page whose choice is read: the id `{{choix:<pageId>}}` carries. */
    pageId: experienceLocalIdSchema,
    operator: z.literal('is-one-of'),
    buttonIds: z.array(experienceLocalIdSchema).min(1).max(EXPERIENCE_BLOCKS_MAX),
  }),
  z.object({
    kind: z.literal('checkbox'),
    /** The Checkbox block: the id `{{champ:<blockId>}}` carries. */
    blockId: experienceLocalIdSchema,
    operator: z.enum(['checked', 'unchecked']),
  }),
  z.object({
    kind: z.literal('input'),
    /** The Input block: the id `{{champ:<blockId>}}` carries. */
    blockId: experienceLocalIdSchema,
    operator: z.enum(['filled', 'empty']),
  }),
  z.object({
    kind: z.literal('objective'),
    /** An objective of the protocol the protocol page plays: `{{objectif:<id>}}`. */
    objectiveId: protocolLocalIdSchema,
    operator: z.enum(['succeeded', 'not-succeeded']),
  }),
]);
export type DisplayCondition = z.infer<typeof displayConditionSchema>;
export type DisplayConditionKind = DisplayCondition['kind'];

/**
 * What every block carries, whatever its type (§9.2, §9.3): its id, its entry
 * animation, its three visual overrides - and its display conditions.
 *
 * A Son ignores `animation` - it has no entry to play, and its inspector's
 * Animation tab carries only « Apparaît au temps ». It is kept on the common
 * shape rather than narrowed away on that one variant: a row that somehow
 * carries `fade` on a sound must stay READABLE, and a narrowed literal would
 * make it a version nobody can open.
 *
 * `conditions` is on the common shape for the reason §9.2 gives - « tout bloc
 * peut porter une liste de conditions » - and DEFAULTED to the empty list, so
 * every version stored before the revue of 2026-09-25 parses unchanged and
 * reads « toujours affiché ». Two blocks refuse any condition all the same, and
 * the refusal is a rule and not a narrower shape (`condition-on-exit`): a
 * Bouton, and a Noyau block in a choice mode - the way out of a page is never
 * hidden (H69).
 */
const blockShape = {
  id: experienceLocalIdSchema,
  animation: blockAnimationSchema.default('fade'),
  visual: blockVisualSchema.default(EMPTY_BLOCK_VISUAL),
  conditions: z.array(displayConditionSchema).max(EXPERIENCE_BLOCK_CONDITIONS_MAX).default([]),
} as const;

/**
 * The eleven types of §9.2, as a version stores them - the Sélection and the Note
 * being the two of issue #176 (retour-experience.md §4): a question and a value
 * the person gives, written like an Input's, with the name of a variable.
 *
 * `assetId` is NULLABLE on the two library blocks, and that is the draft régime
 * of §4 met in the version: an image block exists before a file is picked, and
 * an empty one is a page that shows nothing rather than a version nobody can
 * save. What IS refused is an `assetId` that names a file the library no longer
 * holds (§9.1) - a rule the base answers, not a shape.
 *
 * A variable NAME is only a LABEL (§8.6): the token stored elsewhere carries the
 * block's id. So an empty name is legal here, and what the version refuses is
 * two blocks bearing the SAME name - which is what `@` would then be unable to
 * disambiguate.
 *
 * ## The two of issue #137, and why they carry no `variableName`
 *
 * §9.2 closed the list at six; it held eight, and holds nine. A LIEN is the first block that
 * leaves the dispositif - a Bouton does `next-page` or `next-temps` and never
 * quits it - and a TEXTE COPIABLE is the first whose content is meant to be
 * taken away rather than read. Neither PRODUCES anything: a click that leaves
 * and a click that copies are gestures the interview learns nothing from, so
 * neither carries a `variableName`, and `declaredVariablesOf` never sees them.
 *
 * Both CONSUME variables - a Lien's label like a Checkbox's, a Copiable's text
 * because « ton code est {{champ:code}} » is the whole point of the block - and
 * neither does in its address: `experienceLinkUrlSchema` refuses a token by
 * name, above.
 *
 * ## The ninth, of issue #145: the Noyau block
 *
 * It carries its MODE and, since issue #169, the GAP THRESHOLD its first choice
 * reads (noyau.md §3.2, §3.8), and nothing else. Its texts are not in the
 * block: the two choices and the portrait read the versioned texts of the noyaux
 * (`noyau-texts.ts`), through the ONE reference of the content, `noyauTexts`,
 * which is worth for every Noyau block of the experience (§3.5). What the
 * person reads around the cards - the consigne, an image, a sound - is ordinary
 * blocks of the same page.
 *
 * The « Aperçu avec » of its inspector (N20) is NOT stored: it is the pair the
 * EDITOR previews, a posture of the screen like a selected step, and a version
 * that changed because somebody previewed another pair would be a version
 * nobody meant to write.
 *
 * ## `personName` on the Input (§9.2 of the refonte, H75)
 *
 * « Nom de la personne dans Sessions »: the value of that Input names the
 * session in the list. OPTIONAL and FALSE by default, so a content stored before
 * it keeps parsing; at most one per experience, which is a refusal of
 * `experienceProblems` (`person-name-repeated`) and not a shape.
 */
export const experienceBlockSchema = z.discriminatedUnion('type', [
  z.object({ ...blockShape, type: z.literal('text'), text: richTextSchema }),
  z.object({
    ...blockShape,
    type: z.literal('image'),
    assetId: z.string().min(1).nullable().default(null),
    caption: z.string().trim().max(EXPERIENCE_CAPTION_MAX).default(''),
  }),
  z.object({
    ...blockShape,
    type: z.literal('sound'),
    assetId: z.string().min(1).nullable().default(null),
    loop: z.boolean().default(false),
    volume: z.number().min(0).max(1).default(1),
  }),
  z.object({
    ...blockShape,
    type: z.literal('button'),
    label: z.string().trim().min(1).max(EXPERIENCE_BUTTON_LABEL_MAX),
    action: buttonActionSchema.default('next-page'),
    /**
     * « Libellé d'attente » (noyau.md §4.6, N83, N84; issue #174): what the
     * Bouton says, disabled, while its advance waits - the request, then the
     * determination of the noyau. Plain text without variables, bounded like
     * the label, EMPTY by default and never required: a version stored before
     * #174 has no such field and reads empty, which the reader says
     * « Un instant... ». Only a Bouton that leads to the next page shows it; a
     * « Temps suivant » one keeps it, without effect (N85).
     */
    waitingLabel: z.string().trim().max(EXPERIENCE_BUTTON_LABEL_MAX).default(''),
    /** « Retenir le choix » (§8.6): only meaningful with several buttons on the page. */
    rememberChoice: z.boolean().default(false),
    variableName: z.string().trim().max(EXPERIENCE_VARIABLE_NAME_MAX).default(''),
  }),
  z.object({
    ...blockShape,
    type: z.literal('checkbox'),
    /** Accepts variables (§9.2), so it is parsed for tokens like any other text. */
    label: z.string().trim().min(1).max(EXPERIENCE_CHECKBOX_LABEL_MAX),
    required: z.boolean().default(false),
    variableName: z.string().trim().max(EXPERIENCE_VARIABLE_NAME_MAX).default(''),
  }),
  z.object({
    ...blockShape,
    type: z.literal('input'),
    label: z.string().trim().min(1).max(EXPERIENCE_INPUT_LABEL_MAX),
    lines: inputLinesSchema.default('single'),
    required: z.boolean().default(false),
    /** Only meaningful on ONE line (§9.2); a multi-line input is free text. */
    format: inputFormatSchema.default('text'),
    variableName: z.string().trim().max(EXPERIENCE_VARIABLE_NAME_MAX).default(''),
    /** « Nom de la personne dans Sessions » (H75): false by default, at most one per experience. */
    personName: z.boolean().default(false),
  }),
  z.object({
    ...blockShape,
    type: z.literal('link'),
    /** Bounded like a Bouton's, and accepting variables like a Checkbox's. */
    label: z.string().trim().min(1).max(EXPERIENCE_BUTTON_LABEL_MAX),
    url: experienceLinkUrlSchema,
  }),
  z.object({
    ...blockShape,
    type: z.literal('copyable'),
    /** PLAIN text, bounded like a Texte block, and accepting variables. */
    text: z.string().trim().min(1).max(EXPERIENCE_TEXT_MAX),
  }),
  z.object({
    ...blockShape,
    type: z.literal('noyau'),
    /** REQUIRED in a version: a Noyau block that does not say which of its three it is means nothing. */
    mode: noyauBlockModeSchema,
    /**
     * The gap threshold of the first choice (noyau.md §3.8, N68; issue #169),
     * on EVERY Noyau block and read by the first choice alone. DEFAULTED and
     * never required: a version stored before #169 has no such field, and it
     * reads 5 like a new block (E4) instead of becoming a version nobody can
     * open.
     */
    maxGap: noyauMaxGapSchema.default(NOYAU_MAX_GAP_DEFAULT),
  }),
  z.object({
    ...blockShape,
    type: z.literal('selection'),
    /** Accepts variables like a Checkbox's label (retour-experience.md §4.1). */
    question: z.string().trim().min(1).max(EXPERIENCE_SELECTION_QUESTION_MAX),
    /** 2 to 5, each plain text of 40 characters at most: the COUNT is `selection-options`'s to refuse. */
    options: selectionOptionsSchema.default([]),
    required: z.boolean().default(false),
    variableName: z.string().trim().max(EXPERIENCE_VARIABLE_NAME_MAX).default(''),
  }),
  z.object({
    ...blockShape,
    type: z.literal('note'),
    /** Accepts variables like a Checkbox's label (retour-experience.md §4.2). */
    question: z.string().trim().min(1).max(EXPERIENCE_NOTE_QUESTION_MAX),
    required: z.boolean().default(false),
    variableName: z.string().trim().max(EXPERIENCE_VARIABLE_NAME_MAX).default(''),
  }),
]);
export type ExperienceBlock = z.infer<typeof experienceBlockSchema>;

/* ---------------------------------------------------------------- the temps */

/**
 * When a temps starts (§9.3, H27, H30): after a DELAY counted from the end of
 * the previous temps' animations, or at the CLICK of a button of the previous
 * temps that chose « Joue le temps suivant ».
 */
export const tempsStartSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('delay'),
    /** Seconds, a fraction allowed (« délai 1,5 s »). 0 to 30 (H23). */
    seconds: z.number().min(0).max(EXPERIENCE_TEMPS_DELAY_MAX),
  }),
  z.object({ kind: z.literal('click') }),
]);
export type TempsStart = z.infer<typeof tempsStartSchema>;

/** What the FIRST temps always carries: it starts with the page (§9.3, H27). */
export const FIRST_TEMPS_START: TempsStart = { kind: 'delay', seconds: 0 };

/**
 * A temps (§9.3): what appears together.
 *
 * It has NO animation of its own (H27) - it groups, it carries the RAPIDITÉ of
 * its blocks' animations, and it says when it starts. Each block chooses its
 * own movement and plays it at this speed.
 */
export const tempsSchema = z.object({
  id: experienceLocalIdSchema,
  speed: animationSpeedSchema.default('normal'),
  start: tempsStartSchema.default(FIRST_TEMPS_START),
  blocks: z.array(experienceBlockSchema).max(EXPERIENCE_BLOCKS_MAX).default([]),
});
export type Temps = z.infer<typeof tempsSchema>;

/* ---------------------------------------------------------------- the pages */

/** The transition carried by the EDGE between two pages (§9.3). */
export const pageTransitionSchema = z.object({
  animation: pageTransitionAnimationSchema.default('fade'),
  speed: animationSpeedSchema.default('normal'),
});
export type PageTransition = z.infer<typeof pageTransitionSchema>;

export const DEFAULT_PAGE_TRANSITION: PageTransition = { animation: 'fade', speed: 'normal' };

/**
 * The three kinds of page (§9.1, §9.5; retour-experience.md §2). The protocol
 * page is MANDATORY, UNIQUE and is not deleted; any number of content pages sit
 * before and after it. The FEEDBACK page - issue #176 - is a content page made
 * of ordinary blocks that sits LAST, at most once, and is neither duplicated nor
 * moved: those are rules of `experienceProblems` and not shapes, so a content
 * that breaks them stays readable and says which refusal it earns.
 */
export const pageKindSchema = z.enum(['content', 'protocol', 'feedback']);
export type PageKind = z.infer<typeof pageKindSchema>;

/**
 * A content page PLAYED IN THE CONVERSATION (prolongation.md §3, §8): the page
 * is brought into the chat by the interviewer instead of being drawn on a page,
 * from the context the operator writes on it.
 *
 * `context` is what the interviewer must know to bring THIS page - the same
 * thing, and the same name, as the Contexte of the protocol page (§9.5 of the
 * refonte): 2000 characters at most, variables accepted, resolved when the
 * bubble is written (prolongation.md §3.2). Empty is legal and only warned
 * about (`conversation-context-empty`).
 */
export const pageConversationSchema = z.object({
  context: z.string().trim().max(EXPERIENCE_CONTEXT_MAX).default(''),
});
export type PageConversation = z.infer<typeof pageConversationSchema>;

/**
 * One page: a vertical flow of blocks, grouped into temps (§9.2, §9.3).
 *
 * `protocol` is meaningful on the PROTOCOL page only, and the content
 * refinement below says so. It sits on the page rather than on the content
 * because that is where the operator sets it (§9.5, the page's own editor), and
 * because an experience whose protocol page moved would otherwise carry its
 * protocol in two places.
 *
 * `autoDurationSeconds` is the optional « durée » of §9.2 that advances on its
 * own - for an intertitre. Null is the normal case: one leaves a page by a
 * button. A duration shorter than the last temps' delay is SIGNALLED by the
 * editor and never refused (H27).
 */
export const experiencePageSchema = z.object({
  id: experienceLocalIdSchema,
  name: z.string().trim().min(1).max(EXPERIENCE_PAGE_NAME_MAX),
  kind: pageKindSchema.default('content'),
  autoDurationSeconds: z
    .number()
    .min(EXPERIENCE_AUTO_DURATION_MIN)
    .max(EXPERIENCE_AUTO_DURATION_MAX)
    .nullable()
    .default(null),
  /** The transition that leads to the NEXT page. The last page's is never played. */
  transition: pageTransitionSchema.default(DEFAULT_PAGE_TRANSITION),
  /** What this page overrides of the experience's theme (§9.4). Partial, like the theme. */
  visual: experienceThemeSchema.default({}),
  temps: z.array(tempsSchema).max(EXPERIENCE_TEMPS_MAX).default([]),
  /** `latest` by default, a precise version when pinned (§9.5, vitrine.md §3.1). Null = « sans protocole ». */
  protocol: versionedRefSchema.nullable().default(null),
  /**
   * The PHRASE OF THANKS of the feedback page (retour-experience.md §2), shown
   * in place of its blocks once the feedback is sent. Meaningful on that page
   * only, and OPTIONAL on the wire: a content stored before issue #176 has none
   * on any page, and an absent phrase reads as `DEFAULT_FEEDBACK_THANKS`
   * (`feedbackThanksOf`). Present and EMPTY is the refusal
   * `feedback-thanks-empty`, which the schema leaves to the rule so that the
   * operator reads it named. Plain text, no variable, 600 characters at most.
   */
  thanks: thanksSchema,
  /**
   * « Jouer dans la conversation » (prolongation.md §3, §8): NULL is an
   * ordinary page, which is what every content stored before issue #186 reads
   * as - no migration. Meaningful on a CONTENT page that directly follows the
   * protocol page or another page of the conversation (`conversationRunOf`); a
   * page that breaks that is a refusal of `experienceProblems`
   * (`conversation-page-out-of-run`, `conversation-page-feedback`), not a
   * shape, so the operator reads it named.
   */
  conversation: pageConversationSchema.nullable().default(null),
});
export type ExperiencePage = z.infer<typeof experiencePageSchema>;

/* ------------------------------------------------------ the noyau reference */

/**
 * The reference of an experience to the TEXTS OF THE NOYAUX (noyau.md §3.5,
 * N13; vitrine.md §3.1, H3): a PIN and nothing else.
 *
 * No identifier, where a persona's reference carries one: the texts are ONE
 * versioned object, a singleton (N8), so the id would always be the same - it
 * is `NOYAU_TEXTS_ID` of `noyau-texts.ts`, and the resolution adds it. `latest`
 * by default, a precise version number when pinned, the régime of the persona
 * and the protocol.
 *
 * ONE reference for EVERY Noyau block of the experience, which therefore all
 * play the same version. The editor poses it with the first Noyau block and
 * removes it with the last; what a reader RESOLVES is `noyauTextsPinOf`
 * (`experience/noyau.ts`), which answers null for a content without a Noyau
 * block whatever this field says, and `latest` for one whose field is
 * missing - a hand-written content with a Noyau block and no reference plays
 * the default rather than nothing.
 *
 * `.strict()` like `versionedRefSchema`, the other reference a content stores.
 */
export const noyauTextsRefSchema = z
  .object({
    pin: versionPinSchema,
  })
  .strict();
export type NoyauTextsRef = z.infer<typeof noyauTextsRefSchema>;

/* -------------------------------------------------------------- the persona */

/**
 * The persona of an experience (§10.2): a REFERENCE, plus the overlay that
 * prolongs its direction for this experience only.
 *
 * Never an inline copy: « une seule orthographe du persona, un seul endroit où
 * la corriger ». The overlay is the only place the persona meets the variables
 * of §8.6.
 */
export const experiencePersonaSchema = z.object({
  ref: versionedRefSchema,
  overlay: z.string().trim().max(EXPERIENCE_OVERLAY_MAX).default(''),
});
export type ExperiencePersona = z.infer<typeof experiencePersonaSchema>;

/* -------------------------------------------------------------- the content */

/**
 * The ids a content must keep unique, and where a duplicate would bite: a
 * `{{choix:<pageId>}}` names a page, a `{{champ:<blockId>}}` names a block, and
 * a duplicate would make the address ambiguous for the reader and for the
 * engine alike.
 */
function refuseDuplicateIds(
  content: {
    pages: readonly {
      id: string;
      temps: readonly { id: string; blocks: readonly { id: string }[] }[];
    }[];
  },
  ctx: z.RefinementCtx,
): void {
  const seenPages = new Set<string>();
  const seenBlocks = new Set<string>();
  content.pages.forEach((page, pageIndex) => {
    if (seenPages.has(page.id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pages', pageIndex, 'id'],
        message: 'Page ids are unique inside an experience.',
      });
    }
    seenPages.add(page.id);
    const seenTemps = new Set<string>();
    page.temps.forEach((temps, tempsIndex) => {
      if (seenTemps.has(temps.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pages', pageIndex, 'temps', tempsIndex, 'id'],
          message: 'Temps ids are unique inside a page.',
        });
      }
      seenTemps.add(temps.id);
      temps.blocks.forEach((block, blockIndex) => {
        if (seenBlocks.has(block.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['pages', pageIndex, 'temps', tempsIndex, 'blocks', blockIndex, 'id'],
            message: 'Block ids are unique inside an experience.',
          });
        }
        seenBlocks.add(block.id);
      });
    });
  });
}

/**
 * The two STRUCTURAL invariants of §9.1 and §9.3, refused at the wire.
 *
 * One protocol page, never zero and never two: it is mandatory, unique and
 * undeletable, and the reader would have no answer for a second one. And the
 * first temps of a page starts WITH the page: its delay is 0 and does not
 * adjust, so a stored `click` or a stored 1.5 s on it would be a page whose
 * first blocks never appear.
 *
 * Both are maintained by the editor on every gesture, so only a hand-written
 * body can violate them - which is what a wire-level refinement is for.
 */
function refuseBrokenStructure(
  content: {
    pages: readonly {
      kind: PageKind;
      protocol: unknown;
      temps: readonly { start: TempsStart }[];
    }[];
  },
  ctx: z.RefinementCtx,
): void {
  const protocolPages = content.pages.filter((page) => page.kind === 'protocol');
  if (protocolPages.length !== 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['pages'],
      message: 'An experience carries exactly one protocol page.',
    });
  }

  content.pages.forEach((page, pageIndex) => {
    if (page.kind !== 'protocol' && page.protocol !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pages', pageIndex, 'protocol'],
        message: 'Only the protocol page carries a protocol.',
      });
    }
    const first = page.temps[0];
    if (first !== undefined && (first.start.kind !== 'delay' || first.start.seconds !== 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pages', pageIndex, 'temps', 0, 'start'],
        message: 'The first temps starts with the page: a delay of 0.',
      });
    }
  });
}

/**
 * The object behind the content schema, kept apart because two refinements wrap
 * it: `.partial()` and `.shape` are the object's, and the draft schema and the
 * rename schema below are built from them.
 */
const experienceContentObject = z.object({
  name: z.string().trim().min(1).max(EXPERIENCE_NAME_MAX),
  /**
   * The short description of the cards and of a link's choice page (§13, §15).
   *
   * Allowed EMPTY, where a protocol's is not: an experience is born from the
   * empty editor as well as from the guided form (§13), no refusal of §9.1
   * names it, and a required field nothing refuses would be a 400 no screen
   * could explain.
   */
  description: z.string().trim().max(EXPERIENCE_DESCRIPTION_MAX).default(''),
  theme: experienceThemeSchema.default({}),
  persona: experiencePersonaSchema.nullable().default(null),
  /**
   * The CONTEXTE of the protocol page (§9.5; grill Variables of 2026-09-16,
   * D2): what the interviewer must know about this experience and this person,
   * served every turn after the persona overlay and before the global rules
   * (§10.3). It accepts the four variables of §8.6.
   *
   * On the CONTENT and not on the protocol page, like the overlay and for the
   * same reason: the protocol page is unique (§9.1), so « le Contexte de la page
   * protocole » and « le Contexte de l'expérience » name one thing, and a served
   * text sits beside the other served text rather than beside a page's visual.
   * The editor edits it from the protocol page's inspector all the same.
   *
   * Defaulted to the empty string so every version stored before it parses
   * unchanged, and an empty Contexte serves nothing.
   */
  context: z.string().trim().max(EXPERIENCE_CONTEXT_MAX).default(''),
  /**
   * The reference to the texts of the noyaux (noyau.md §3.5, N13), or null.
   *
   * Defaulted to null so every version stored before issue #145 parses
   * unchanged - they carry no Noyau block, and null is what they mean.
   */
  noyauTexts: noyauTextsRefSchema.nullable().default(null),
  pages: z.array(experiencePageSchema).min(1).max(EXPERIENCE_PAGES_MAX),
});

/**
 * The CONTENT of one version - the totality of the experience at that version,
 * name included (§4.1).
 *
 * **Deliberately not `.strict()`** (§4.1): this schema parses rows written by
 * earlier versions of the code, and a strict object refuses a row the day a
 * field is added. Every nested shape follows the same rule.
 */
/**
 * The 4000 characters of H23, measured where the operator types them.
 *
 * It is a refinement of the CONTENT and not of the block, and that is forced by
 * the shape: `experienceBlockSchema` is a `discriminatedUnion`, whose members
 * must stay plain objects - a `.superRefine` on the Texte member would make it
 * a `ZodEffects` the union refuses to build.
 */
function refuseOversizedText(
  content: { pages: readonly { temps: readonly { blocks: readonly ExperienceBlock[] }[] }[] },
  ctx: z.RefinementCtx,
): void {
  content.pages.forEach((page, pageIndex) => {
    page.temps.forEach((temps, tempsIndex) => {
      temps.blocks.forEach((block, blockIndex) => {
        if (block.type !== 'text') return;
        if (richTextPlain(block.text).length <= EXPERIENCE_TEXT_MAX) return;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pages', pageIndex, 'temps', tempsIndex, 'blocks', blockIndex, 'text'],
          message: `A static text holds at most ${EXPERIENCE_TEXT_MAX} characters.`,
        });
      });
    });
  });
}

export const experienceContentSchema = experienceContentObject
  .superRefine(refuseDuplicateIds)
  .superRefine(refuseBrokenStructure)
  .superRefine(refuseOversizedText);
export type ExperienceContent = z.infer<typeof experienceContentSchema>;

/* ---------------------------------------------------------------- the draft */

/**
 * A block as a DRAFT holds it: the labels allowed empty while somebody types
 * them, and a Lien's address refused on the variable alone - the reason is on
 * `experienceLinkUrlDraftSchema`.
 */
export const experienceBlockDraftSchema = z.discriminatedUnion('type', [
  z.object({ ...blockShape, type: z.literal('text'), text: richTextSchema }),
  z.object({
    ...blockShape,
    type: z.literal('image'),
    assetId: z.string().min(1).nullable().default(null),
    caption: z.string().trim().max(EXPERIENCE_CAPTION_MAX).default(''),
  }),
  z.object({
    ...blockShape,
    type: z.literal('sound'),
    assetId: z.string().min(1).nullable().default(null),
    loop: z.boolean().default(false),
    volume: z.number().min(0).max(1).default(1),
  }),
  z.object({
    ...blockShape,
    type: z.literal('button'),
    label: z.string().trim().max(EXPERIENCE_BUTTON_LABEL_MAX).default(''),
    action: buttonActionSchema.default('next-page'),
    /** The version's, word for word: empty is already its default (issue #174). */
    waitingLabel: z.string().trim().max(EXPERIENCE_BUTTON_LABEL_MAX).default(''),
    rememberChoice: z.boolean().default(false),
    variableName: z.string().trim().max(EXPERIENCE_VARIABLE_NAME_MAX).default(''),
  }),
  z.object({
    ...blockShape,
    type: z.literal('checkbox'),
    label: z.string().trim().max(EXPERIENCE_CHECKBOX_LABEL_MAX).default(''),
    required: z.boolean().default(false),
    variableName: z.string().trim().max(EXPERIENCE_VARIABLE_NAME_MAX).default(''),
  }),
  z.object({
    ...blockShape,
    type: z.literal('input'),
    label: z.string().trim().max(EXPERIENCE_INPUT_LABEL_MAX).default(''),
    lines: inputLinesSchema.default('single'),
    required: z.boolean().default(false),
    format: inputFormatSchema.default('text'),
    variableName: z.string().trim().max(EXPERIENCE_VARIABLE_NAME_MAX).default(''),
    personName: z.boolean().default(false),
  }),
  z.object({
    ...blockShape,
    type: z.literal('link'),
    label: z.string().trim().max(EXPERIENCE_BUTTON_LABEL_MAX).default(''),
    url: experienceLinkUrlDraftSchema,
  }),
  z.object({
    ...blockShape,
    type: z.literal('copyable'),
    text: z.string().trim().max(EXPERIENCE_TEXT_MAX).default(''),
  }),
  z.object({
    ...blockShape,
    type: z.literal('noyau'),
    /** A block somebody has just added is a first choice until its inspector says otherwise. */
    mode: noyauBlockModeSchema.default('first-choice'),
    /** Bounded like the version's: the inspector clamps while one types (noyau.md §3.8, N68). */
    maxGap: noyauMaxGapSchema.default(NOYAU_MAX_GAP_DEFAULT),
  }),
  z.object({
    ...blockShape,
    type: z.literal('selection'),
    question: z.string().trim().max(EXPERIENCE_SELECTION_QUESTION_MAX).default(''),
    options: selectionOptionsSchema.default([]),
    required: z.boolean().default(false),
    variableName: z.string().trim().max(EXPERIENCE_VARIABLE_NAME_MAX).default(''),
  }),
  z.object({
    ...blockShape,
    type: z.literal('note'),
    question: z.string().trim().max(EXPERIENCE_NOTE_QUESTION_MAX).default(''),
    required: z.boolean().default(false),
    variableName: z.string().trim().max(EXPERIENCE_VARIABLE_NAME_MAX).default(''),
  }),
]);
export type ExperienceBlockDraft = z.infer<typeof experienceBlockDraftSchema>;

export const tempsDraftSchema = tempsSchema.extend({
  blocks: z.array(experienceBlockDraftSchema).max(EXPERIENCE_BLOCKS_MAX).default([]),
});
export type TempsDraft = z.infer<typeof tempsDraftSchema>;

/** A page as a DRAFT holds it: the name allowed empty, the temps as drafts. */
export const experiencePageDraftSchema = experiencePageSchema.extend({
  name: z.string().trim().max(EXPERIENCE_PAGE_NAME_MAX).default(''),
  temps: z.array(tempsDraftSchema).max(EXPERIENCE_TEMPS_MAX).default([]),
});
export type ExperiencePageDraft = z.infer<typeof experiencePageDraftSchema>;

/**
 * The content of a DRAFT: the same fields, each optional, the pages as drafts.
 *
 * It is what the editor SENDS and what it READS BACK: one schema for both
 * halves of the round trip. The bounds still apply to what IS there. The two
 * refinements are left out: a draft is parsed on every reload, and a page
 * without its protocol page - the state of an editor mid-gesture - is a defect
 * the version save refuses anyway.
 */
export const experienceDraftContentSchema = z.object({
  name: experienceContentObject.shape.name.optional(),
  description: experienceContentObject.shape.description.optional(),
  theme: experienceThemeSchema.optional(),
  persona: experiencePersonaSchema.nullable().optional(),
  context: z.string().trim().max(EXPERIENCE_CONTEXT_MAX).optional(),
  noyauTexts: noyauTextsRefSchema.nullable().optional(),
  pages: z.array(experiencePageDraftSchema).max(EXPERIENCE_PAGES_MAX).optional(),
});
export type ExperienceDraftContent = z.infer<typeof experienceDraftContentSchema>;

/* ---------------------------------------------------------------- the cards */

/**
 * A reference the DEVELOPMENT version cites and that has been retired (§4,
 * §15): what the card's warning line names - « remplacer le persona Voix
 * neutre, retiré le 8 sept. ». Computed server side, because only the server
 * holds both halves of the comparison.
 */
export const experienceRetiredReferenceSchema = z
  .object({
    kind: z.enum(['protocol', 'persona']),
    id: z.string().min(1),
    name: z.string().min(1),
    retiredAt: z.string().datetime(),
  })
  .strict();
export type ExperienceRetiredReference = z.infer<typeof experienceRetiredReferenceSchema>;

/**
 * One card of the list (§4, §15): the thirteen common fields of
 * `versionedCardShape`, plus the facets line an experience draws - « 5 pages ·
 * Entretien fondateur · Claire empathique ».
 *
 * `protocolName` and `personaName` are NULL when there is none, and the screen
 * words « sans protocole » / « sans persona » from that - the identifier is
 * code, the French is the page's (mockup: both are lower case, « comme des
 * valeurs »). Null is also what an id the base no longer holds gives: the card
 * stays readable, and the retired ones are named on the warning line.
 *
 * `linkCount` is the usage of §4 for an experience: its referents are not
 * versioned objects but the ACTIVE LINKS of §5.1 (H25), and issue #59 fills it
 * - it is the number of DISTINCT active links whose entries name this
 * experience, whatever tag each plays, and the card hides the line when it is
 * nul (« joué par 2 liens », absent à zéro). A revoked or expired link is not
 * counted: it can make nobody enter, so it is no longer a usage.
 *
 * It stays a field of its own rather than a line of the common `usage` counts:
 * `versionedUsageCountSchema` is keyed on `versionedKindSchema`, a link is not
 * a versioned kind, and §15 draws this fact as its own phrase on the card.
 */
export const experienceCardSchema = z
  .object({
    ...versionedCardShape,
    description: z.string(),
    pageCount: z.number().int().nonnegative(),
    protocolName: z.string().min(1).nullable(),
    personaName: z.string().min(1).nullable(),
    linkCount: z.number().int().nonnegative(),
    retiredReferences: z.array(experienceRetiredReferenceSchema),
  })
  .strict();
export type ExperienceCard = z.infer<typeof experienceCardSchema>;

/** The list, served WHOLE - retired entries included - and filtered in the page (§4). */
export const experienceListSchema = z
  .object({ experiences: z.array(experienceCardSchema) })
  .strict();
export type ExperienceList = z.infer<typeof experienceListSchema>;

/**
 * Everything the editor reads in one call - and the ONE object whose « utilisé
 * dans » actually carries a line that is not a versioned object.
 *
 * The detail builder spells `usage` with `usageLineSchema`, which is
 * `versionedUsageSchema` plus the `link` kind of §4/H25, for the four objects
 * alike (one type, one panel). An experience's referents ARE its active links:
 * they are what the panel names (« Lien « Campagne septembre » » with the
 * pastille of the tag it plays) and what the two pointer boxes list; the three
 * other objects never receive one, because a link cites an experience and
 * nothing else - see the note beside `usageKindSchema`.
 */
export const experienceDetailSchema = versionedDetailSchema(
  experienceContentSchema,
  experienceDraftContentSchema,
);
export type ExperienceDetail = z.infer<typeof experienceDetailSchema>;

/** One version read in full, for « Voir » and « Repartir de cette version ». */
export const experienceVersionSchema = versionedContentSchema(experienceContentSchema);
export type ExperienceVersion = z.infer<typeof experienceVersionSchema>;

/** « Enregistrer une nouvelle version », with its optional note and its production checkbox. */
export const experienceSaveVersionSchema = saveVersionRequestSchema(experienceContentSchema);
export type ExperienceSaveVersion = z.infer<typeof experienceSaveVersionSchema>;

/** An autosave of the draft, on the partial content of `experienceDraftContentSchema`. */
export const experienceSaveDraftSchema = saveDraftRequestSchema(experienceDraftContentSchema);
export type ExperienceSaveDraft = z.infer<typeof experienceSaveDraftSchema>;

/** Renaming, on the experience's OWN name field - see `renameRequestSchemaFor`. */
export const experienceRenameSchema = renameRequestSchemaFor(experienceContentObject.shape.name);
export type ExperienceRename = z.infer<typeof experienceRenameSchema>;

/* -------------------------------------------------------------- the filters */

/** The four common sorts of §4, and no more: « aucun tri propre » (§15). */
export const experienceSortSchema = versionedSortSchema;
export type ExperienceSort = z.infer<typeof experienceSortSchema>;

export const EXPERIENCE_DEFAULT_SORT: ExperienceSort = VERSIONED_DEFAULT_SORT;

/** « Lien actif » (§15), the third own filter - a yes/no over the links of §5.1. */
export const experienceLinkFilterSchema = z.enum(['all', 'yes', 'no']);
export type ExperienceLinkFilter = z.infer<typeof experienceLinkFilterSchema>;

/**
 * The thirteen common filters of §4 plus the experience's three (§15), as ONE
 * object.
 *
 * `referencedProtocolId` and `referencedPersonaId` are spelled long on purpose:
 * `versionedFiltersShape` already carries a `protocolId`, and it means the
 * OPPOSITE - the referent that uses this object. An experience's own two
 * facets are the references it CARRIES, which is the other direction of the
 * same arrow, and two fields called `protocolId` on one object would be read
 * wrong exactly once.
 *
 * `activeLink` reads the card's `linkCount` and nothing else: « oui » is a
 * count above zero. It is a facet of the LIST and stays a pure filter over the
 * cards, at the régime of every other filter of §4 - the server serves the
 * library whole, the page filters it.
 */
export const experienceFiltersSchema = z
  .object({
    ...versionedFiltersShape,
    referencedProtocolId: z.string().min(1).nullable().default(null),
    referencedPersonaId: z.string().min(1).nullable().default(null),
    activeLink: experienceLinkFilterSchema.default('all'),
    sort: experienceSortSchema.default(EXPERIENCE_DEFAULT_SORT),
  })
  .strict();
export type ExperienceFilters = z.infer<typeof experienceFiltersSchema>;

/** The neutral state: every experience, retired excluded, sorted by name. */
export const EXPERIENCE_FILTERS_DEFAULT: ExperienceFilters = experienceFiltersSchema.parse({});

/* ------------------------------------------------------------- the refusals */

/**
 * WHERE a problem sits, so the box can link to it (« Ouvrir » on each line of
 * the refusal encart, mockup `EnregistrerVersion.dc.html`).
 *
 * Every field is NULLABLE, and that is not laziness: the background image of
 * the THEME is cited by no page and by no block, and a missing file there has
 * to be reportable all the same. `pageIndex` is ZERO-BASED; the screen adds
 * one, like the rail does.
 */
const problemPlace = {
  pageId: experienceLocalIdSchema.nullable(),
  pageIndex: z.number().int().nonnegative().nullable(),
  pageName: z.string().nullable(),
  blockId: experienceLocalIdSchema.nullable(),
} as const;

/**
 * The five kinds of variable an experience's texts may carry (§8.6): the four
 * of the grill Variables, and `noyau` - the retained noyau of noyau.md §3.3,
 * `{{noyau}}`, in reach from the page AFTER the first choice.
 */
export const experienceVariableKindSchema = z.enum([
  'field',
  'choice',
  'objective',
  'criterion',
  'noyau',
]);
export type ExperienceVariableKind = z.infer<typeof experienceVariableKindSchema>;

/**
 * WHY a variable is out of reach where it is cited (§8.6, grill Variables D3):
 * cited on the very page that declares it, whose value does not exist while
 * that page is shown; cited before the page that declares it; an objective
 * cited on a page before the protocol page, where it would always render
 * nothing. The rule itself is `variableScopeBreach` in `experience/variables.ts`.
 */
export const variableScopeReasonSchema = z.enum([
  'same-page',
  'before-declaration',
  'before-protocol',
]);
export type VariableScopeReason = z.infer<typeof variableScopeReasonSchema>;

/**
 * What « Enregistrer une nouvelle version » refuses in an experience (§9.1),
 * one entry per problem, each one NAMING its place.
 *
 * The four are the letter of §9.1, and each `kind` IS the code the screen words
 * its line from - the same arrangement as `protocolProblemSchema`, where the
 * umbrella `code` of the body says which catalogue is speaking and the `kind`
 * says what is wrong.
 *
 * `unresolved-variable` carries the TOKEN as stored, because that is what the
 * operator will search the editor for, and the kind plus the id it points at.
 * `duplicate-variable-name` is reported once per OFFENDING block - the second
 * and every later one - so every line of the box links somewhere.
 *
 * `variable-out-of-scope` is the fifth refusal, added by the grill Variables of
 * 2026-09-16 (D3): the variable resolves, but not WHERE it is cited - the
 * person would see the raw token. It carries the place of the citation like
 * `unresolved-variable`, plus the page that declares the variable, because the
 * line of the box names both: « @email est cité page 1, mais saisi page 4 ».
 * Reported once per occurrence, for the same reason. For `@noyau` the declaring
 * page is the page of the FIRST CHOICE (noyau.md §3.3).
 *
 * `inCondition` marks the two variable refusals when the citation is a DISPLAY
 * CONDITION of the block and not a token of its text (§9.2 of the refonte, revue
 * du 2026-09-25): the rule is the pill's, word for word, and the line of the box
 * is not - « La condition du bloc Image, page 5, cite @me recontacter, qui est
 * saisi page 5 ». Absent on a text citation, so every body written before it
 * reads as what it always was.
 *
 * ## The refusals of the noyau (noyau.md §3.4, N9, N10)
 *
 * Each names the PAGE, the régime of §9.1, and the page that makes it a refusal
 * when there is one - the protocol page, the first choice, the first holder:
 *
 * - `noyau-before-protocol` - a Noyau block on a page that does not come AFTER
 *   the protocol page;
 * - `noyau-choice-repeated` - a second block of the same choice mode in the
 *   experience, reported on the second and every later one;
 * - `noyau-first-choice-missing` - a second choice, a portrait, a `@noyau` or a
 *   condition on `@noyau` with no first choice to give a retained noyau;
 * - `noyau-out-of-order` - a second choice or a portrait on a page that does not
 *   come AFTER the first choice's;
 * - `noyau-choice-page-exit` - a page carrying a Noyau block in a choice mode
 *   and, besides it, another choice, a Bouton that changes page or an automatic
 *   duration: the choice is the ONLY way out. A Bouton that plays the next
 *   temps stays allowed (an introduction, then the cards);
 * - `noyau-choice-last-page` - a Noyau block in a choice mode on the LAST page
 *   of the timeline: its button « confirme et passe à la page suivante » (§3.2),
 *   so a page of choice always has one after it. On the last, the session would
 *   end on arrival and no choice would ever be recorded (review of !464);
 * - `condition-on-exit` - a display condition on the way out of a page, a Bouton
 *   of either action or a Noyau block in a choice mode (H69).
 *
 * `person-name-repeated` (H75) is the refusal of the second « Nom de la personne
 * dans Sessions »: at most one per experience, reported on every holder after
 * the first, with the first's page - « Deux champs portent « Nom de la personne
 * dans Sessions », pages 1 et 3 ».
 *
 * ## The seven refusals of the feedback page (retour-experience.md §7.3, #176)
 *
 * Each names the PAGE, and the block when one is at fault:
 *
 * - `feedback-repeated` - a second feedback page, reported on it with the first's;
 * - `feedback-not-last` - a feedback page that is not the last of the timeline;
 * - `feedback-submit-missing` - a feedback page without EXACTLY one « Envoyer le
 *   retour » Bouton; `submitCount` says whether none (0) or several;
 * - `submit-outside-feedback` - an « Envoyer le retour » Bouton on a page that is
 *   not the feedback page, named on the Bouton;
 * - `feedback-page-exit` - a feedback page that LEAVES: a Bouton that changes
 *   page, named on the Bouton, or an automatic duration. It is sent, not left;
 * - `feedback-thanks-empty` - a phrase of thanks that holds nothing;
 * - `selection-options` - a Sélection with fewer than two or more than five
 *   options, an empty option, or two options of the same label, named on the
 *   block; `reason` says which, one line per reason.
 *
 * ## The six refusals of a page played in the conversation (prolongation.md §3.4, #186)
 *
 * Each names the PAGE, and the block when one is at fault:
 *
 * - `conversation-page-out-of-run` - a ticked page that does not follow the
 *   protocol page or another page of the conversation directly;
 * - `conversation-page-input` - a page of the conversation carrying an Input,
 *   named on the Input: the person types nothing there;
 * - `conversation-page-auto-duration` - a page of the conversation with an
 *   automatic duration: it would advance without a click;
 * - `conversation-page-next-temps` - a page of the conversation carrying a
 *   « temps suivant » Bouton, named on the Bouton: the conversation has no temps;
 * - `conversation-page-no-exit` - a page of the conversation with no way out
 *   a click can take (a « page suivante » Bouton, a Noyau block in a choice
 *   mode), unless it is the last page of the timeline;
 * - `conversation-page-feedback` - a page that is not a content page and is
 *   ticked, the feedback page first among them.
 */
const variableCitation = {
  ...problemPlace,
  token: z.string().min(1),
  variableKind: experienceVariableKindSchema,
  variableId: z.string().min(1),
  /** The citation is a display condition of the block, not a token of its text (§9.2). */
  inCondition: z.literal(true).optional(),
} as const;

/** The page that makes a noyau refusal one: the protocol page, the first choice, the first holder. */
const referencePage = {
  pageIndex: z.number().int().nonnegative().nullable(),
  pageName: z.string().nullable(),
} as const;

export const experienceProblemSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('protocol-without-persona') }).strict(),
  z
    .object({
      kind: z.literal('unresolved-variable'),
      ...variableCitation,
    })
    .strict(),
  z
    .object({
      kind: z.literal('variable-out-of-scope'),
      ...variableCitation,
      reason: variableScopeReasonSchema,
      /** The page that declares the variable - the protocol page for `before-protocol`. */
      declaredPageIndex: z.number().int().nonnegative().nullable(),
      declaredPageName: z.string().nullable(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('missing-asset'),
      ...problemPlace,
      assetId: z.string().min(1),
    })
    .strict(),
  z
    .object({
      kind: z.literal('duplicate-variable-name'),
      ...problemPlace,
      name: z.string().min(1),
    })
    .strict(),
  z
    .object({
      kind: z.literal('person-name-repeated'),
      ...problemPlace,
      /** The first Input that carries the flag, which keeps it. */
      firstPage: z.object(referencePage).strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('noyau-before-protocol'),
      ...problemPlace,
      /** The protocol page the block has to come after; null only on a content that has none. */
      protocolPage: z.object(referencePage).strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('noyau-choice-repeated'),
      ...problemPlace,
      mode: noyauChoiceModeSchema,
      /** The first block of that mode, which keeps it. */
      firstPage: z.object(referencePage).strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('noyau-first-choice-missing'),
      ...problemPlace,
      /** What needs a retained noyau: a block of either mode, a `@noyau` of a text, or a condition. */
      needs: z.enum(['second-choice', 'portrait', 'variable', 'condition']),
    })
    .strict(),
  z
    .object({
      kind: z.literal('noyau-out-of-order'),
      ...problemPlace,
      mode: z.enum(['second-choice', 'portrait']),
      /** The page of the first choice, which this block has to come after. */
      firstChoicePage: z.object(referencePage).strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('noyau-choice-page-exit'),
      ...problemPlace,
      /** The OTHER way out: `blockId` names it, and is null for the automatic duration. */
      exit: z.enum(['choice', 'next-page-button', 'auto-duration']),
    })
    .strict(),
  z
    .object({
      /** `blockId` names the block of choice: the page's way out, with no page to lead to. */
      kind: z.literal('noyau-choice-last-page'),
      ...problemPlace,
    })
    .strict(),
  z
    .object({
      kind: z.literal('condition-on-exit'),
      ...problemPlace,
      blockType: z.enum(['button', 'noyau']),
    })
    .strict(),
  z
    .object({
      kind: z.literal('feedback-repeated'),
      ...problemPlace,
      /** The first feedback page, which keeps it. */
      firstPage: z.object(referencePage).strict(),
    })
    .strict(),
  z.object({ kind: z.literal('feedback-not-last'), ...problemPlace }).strict(),
  z
    .object({
      kind: z.literal('feedback-submit-missing'),
      ...problemPlace,
      /** How many « Envoyer le retour » Boutons the page holds: 0, or 2 and more. */
      submitCount: z.number().int().nonnegative(),
    })
    .strict(),
  z.object({ kind: z.literal('submit-outside-feedback'), ...problemPlace }).strict(),
  z
    .object({
      kind: z.literal('feedback-page-exit'),
      ...problemPlace,
      /** `next-page-button` names the Bouton in `blockId`; `auto-duration` has none. */
      exit: z.enum(['next-page-button', 'auto-duration']),
    })
    .strict(),
  z.object({ kind: z.literal('feedback-thanks-empty'), ...problemPlace }).strict(),
  z.object({ kind: z.literal('conversation-page-out-of-run'), ...problemPlace }).strict(),
  z.object({ kind: z.literal('conversation-page-input'), ...problemPlace }).strict(),
  z.object({ kind: z.literal('conversation-page-auto-duration'), ...problemPlace }).strict(),
  z.object({ kind: z.literal('conversation-page-next-temps'), ...problemPlace }).strict(),
  z.object({ kind: z.literal('conversation-page-no-exit'), ...problemPlace }).strict(),
  z.object({ kind: z.literal('conversation-page-feedback'), ...problemPlace }).strict(),
  z
    .object({
      kind: z.literal('selection-options'),
      ...problemPlace,
      reason: z.enum(['too-few', 'too-many', 'empty-option', 'duplicate-option']),
    })
    .strict(),
]);
export type ExperienceProblem = z.infer<typeof experienceProblemSchema>;
export type ExperienceProblemKind = ExperienceProblem['kind'];

/**
 * The objects an experience references, for the production checks below: the
 * protocol of its protocol page, its persona - and, since issue #151, the TEXTS
 * OF THE NOYAUX, cited by the one reference `noyauTexts` of a content that
 * carries a Noyau block (noyau.md §3.5). « Passer en production » of the
 * experience checks that they resolve in production, and the save box warns
 * while they have nothing there, at the régime of the two others.
 *
 * ADDITIVE: every blocker and warning written before names one of the first
 * two, and still parses.
 */
export const experienceReferenceKindSchema = z.enum(['protocol', 'persona', 'noyau-texts']);
export type ExperienceReferenceKind = z.infer<typeof experienceReferenceKindSchema>;

const blockerPlace = {
  reference: experienceReferenceKindSchema,
  id: z.string().min(1),
  /** The entity's current name, or the empty string for an id the base does not hold. */
  name: z.string(),
} as const;

/**
 * The protocol of an experience that plays the noyau sequence has NO READING
 * for one of the two passes of the analyst in the version the experience cites
 * it at (noyau.md §3.7, N36; issue #154; analyste.md §3.6, K17, issue #196) - a
 * warning of the save box and a blocker of « Passer en production », under one
 * code and one shape, the régime of `reference-without-production`.
 *
 * It names the protocol, the version its pin resolves to and the PASS whose
 * reading is empty - « Le protocole « Scénario de succès » (v18) n'a pas de
 * lecture pour la passe 2 de l'analyste » - so the line can link to that
 * protocol's Analyste section. One line per empty reading, the first pass's then
 * the second's. There is no reading by default (P5): no session of the experience
 * starts until the protocol has both, which is the refusal at start-up of the
 * cascade.
 */
const analystReadingMissingSchema = z
  .object({
    kind: z.literal('analyst-reading-missing'),
    reference: z.literal('protocol'),
    id: z.string().min(1),
    name: z.string(),
    /** What the experience cites the protocol at - the version the fact is read on. */
    pin: versionPinSchema,
    versionNumber: z.number().int().positive(),
    /** The pass whose reading is empty: 1, the functions of each objective, or 2, the style of the discourse. */
    pass: analystPassSchema,
  })
  .strict();

/**
 * The protocol of an experience that plays the noyau sequence has a LEVEL that no
 * objective carries, in the version the experience cites it at (analyste.md §3.6,
 * G21, K17; issue #196) - a warning of the save box and a blocker of « Passer en
 * production », under one code and one shape, like `analyst-reading-missing`.
 *
 * It names the protocol, the version its pin resolves to and every level that has
 * no objective, in the order Pourquoi, Comment, Quoi - « Le protocole « Scénario
 * de succès » (v18) n'a aucun objectif au niveau Comment » -, at least one.
 */
const analystLevelsMissingSchema = z
  .object({
    kind: z.literal('analyst-levels-missing'),
    reference: z.literal('protocol'),
    id: z.string().min(1),
    name: z.string(),
    /** What the experience cites the protocol at - the version the fact is read on. */
    pin: versionPinSchema,
    versionNumber: z.number().int().positive(),
    levels: z.array(analystLevelSchema).min(1),
  })
  .strict();

/**
 * The protocol an experience plays cites, in a DIRECTION of one of its steps,
 * an objective that version no longer carries (§8.6 of the refonte, noyau.md
 * §4.9, V5, V6, N53; issue #158) - a warning of the save box and a blocker of
 * « Passer en production », under one code and one shape, the régime of
 * `analyst-reading-missing`: read on the version the experience's pin resolves
 * to when the gesture is made.
 *
 * One per STEP, named with the protocol and that version - « Le protocole
 * « Entretien fondateur » (production v5) cite, dans la direction de l'étape
 * « Émergence », un objectif qui n'existe plus » - so the line can open the
 * protocol. Never refused at the start of a session (N54): the direction is
 * served to the interviewer as before. An orphan of the READING is not here: it
 * is said on the protocol's side only (V2).
 */
const protocolVariableOrphanSchema = z
  .object({
    kind: z.literal('protocol-variable-orphan'),
    reference: z.literal('protocol'),
    id: z.string().min(1),
    name: z.string(),
    /** What the experience cites the protocol at - the version the fact is read on. */
    pin: versionPinSchema,
    versionNumber: z.number().int().positive(),
    stepId: protocolLocalIdSchema,
    stepIndex: z.number().int().nonnegative(),
    stepName: z.string(),
  })
  .strict();

/**
 * What « Passer en production » refuses (§4, strict resolution; §9.1): a
 * `latest` reference whose object has no production version, a retired one (it
 * resolves to nothing, pinned or not), an id the base does not hold at all -
 * and the one refusal that is not about a reference, an EMPTY protocol page.
 *
 * The empty protocol page is permitted in development and refused in production
 * (§9.1): a suite of pages with no conversation is enough to try a theme, and
 * is not something a recruited person should be sent to.
 *
 * Since issue #154, a fifth: the protocol of an experience that plays the noyau
 * sequence has no reading for the analyst (`analyst-reading-missing`, above; one
 * per empty pass since #196).
 * Since issue #158, a sixth: a direction of the protocol cites an objective
 * the version no longer carries (`protocol-variable-orphan`, above).
 * Since issue #196, a level that no objective carries (`analyst-levels-missing`,
 * above).
 */
export const experienceProductionBlockerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('reference-without-production'), ...blockerPlace }).strict(),
  z
    .object({
      kind: z.literal('reference-retired'),
      ...blockerPlace,
      retiredAt: z.string().datetime(),
    })
    .strict(),
  z.object({ kind: z.literal('reference-unknown'), ...blockerPlace }).strict(),
  z
    .object({
      kind: z.literal('empty-protocol-page'),
      pageId: experienceLocalIdSchema,
      pageIndex: z.number().int().nonnegative(),
      pageName: z.string(),
    })
    .strict(),
  analystLevelsMissingSchema,
  analystReadingMissingSchema,
  protocolVariableOrphanSchema,
]);
export type ExperienceProductionBlocker = z.infer<typeof experienceProductionBlockerSchema>;

/**
 * An AVERTISSEMENT, never a refusal (§9.1).
 *
 * The one of the references - « le persona n'a jamais été passé en production »
 * - is gone with the tags (vitrine.md §3.2): a `latest` reference plays « latest
 * dev » on the atelier, so it always resolves there, and what the production
 * lacks is `reference-without-production` above, on the production gesture.
 *
 * Two since issue #151, both about the noyau sequence and both warnings
 * and never refusals (noyau.md §3.4, §3.5):
 *
 * - `noyau-texts-missing` - the texts of the noyaux have NO version at all: no
 *   session of an experience that carries a Noyau block starts, in
 *   development as in production, until the first « Enregistrer une nouvelle
 *   version » of Réglages > Noyaux (§6.4). The blocker of the production
 *   gesture is `reference-unknown` on them;
 * - `noyau-sequence-skipped` - the protocol page is EMPTY and a Noyau block is
 *   posed: the experience plays, and its sequence is always skipped, since
 *   nothing will have been gathered - « la page protocole est vide, la séquence
 *   noyau sera toujours sautée ». It names the protocol page.
 *
 * And one since issue #154, `analyst-reading-missing` (noyau.md §3.7): the
 * protocol cited under its tag has no reading for the analyst, so no session
 * of the experience will start until it has one. The same fact refuses « Passer
 * en production », under the same code - and the box says it ONCE: ticked, the
 * yellow line gives way to the red one.
 *
 * And one since issue #158, `protocol-variable-orphan` (§8.6 of the refonte):
 * a direction of the protocol cites an objective the version no longer
 * carries, per step - the same fact refuses « Passer en production », under the
 * same code, said once in the box as well.
 *
 * And one since issue #186, `conversation-context-empty` (prolongation.md
 * §3.4): a page played in the conversation whose context is empty - the
 * interviewer will bring the page without any indication. A warning and never a
 * refusal: the version stays playable.
 */
export const experienceWarningSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('noyau-texts-missing') }).strict(),
  z
    .object({
      kind: z.literal('conversation-context-empty'),
      pageId: experienceLocalIdSchema,
      pageIndex: z.number().int().nonnegative(),
      pageName: z.string(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('noyau-sequence-skipped'),
      pageId: experienceLocalIdSchema,
      pageIndex: z.number().int().nonnegative(),
      pageName: z.string(),
    })
    .strict(),
  analystLevelsMissingSchema,
  analystReadingMissingSchema,
  protocolVariableOrphanSchema,
]);
export type ExperienceWarning = z.infer<typeof experienceWarningSchema>;

/**
 * The two refusals that belong to the experience and to nothing else, as one
 * closed catalogue - the same doctrine as `protocolErrorCodeSchema`, and
 * deliberately NOT the same enum as §4: these are states of a CONTENT, not of
 * the base.
 */
export const experienceErrorCodeSchema = z.enum(['invalid-timeline', 'production-unresolved']);
export type ExperienceErrorCode = z.infer<typeof experienceErrorCodeSchema>;

/**
 * The body of an experience refusal: the code, and the LIST behind it -
 * problems for `invalid-timeline`, blockers for `production-unresolved`. Both
 * arrays are always present so a reader never branches on an absent key.
 */
export const experienceErrorSchema = z
  .object({
    error: z.string().min(1),
    code: experienceErrorCodeSchema,
    problems: z.array(experienceProblemSchema).default([]),
    blockers: z.array(experienceProductionBlockerSchema).default([]),
  })
  .strict();
export type ExperienceError = z.infer<typeof experienceErrorSchema>;

/* ------------------------------------------------------- the save-box check */

/**
 * The DRY RUN the save box asks for before it lets the button be pressed
 * (mockup `EnregistrerVersion.dc.html`: a red encart of refusals, a yellow one
 * of warnings, « Enregistrer la v6 » inactive while a refusal stands).
 *
 * It is the ELEVENTH route of `/api/experiences`, beside the ten of §4, and it
 * exists because of the AVERTISSEMENT of §9.1: a warning is not a refusal, so
 * it can never travel as a 409, and the box has to read it BEFORE it posts.
 * Two of the four §9.1 refusals also need the base - « ce fichier existe-t-il
 * encore », « ce persona a-t-il une version en production » - so the editor
 * cannot draw the box from what it holds alone.
 *
 * It WRITES NOTHING. The content it carries is the one the box is about to
 * save, and not the server's draft, because those two differ by exactly the
 * keystrokes the autosave has not landed yet.
 */
export const experienceCheckRequestSchema = z
  .object({
    content: experienceContentSchema,
    /** « Passer aussi cette version en production », cleared by default (§4). */
    alsoProduction: z.boolean().default(false),
  })
  .strict();
export type ExperienceCheckRequest = z.infer<typeof experienceCheckRequestSchema>;

/**
 * What the box draws. `blockers` is empty unless `alsoProduction` was set:
 * those are refusals of the production gesture, and listing them on a plain
 * save would grey a button nothing is wrong with.
 */
export const experienceCheckSchema = z
  .object({
    problems: z.array(experienceProblemSchema),
    blockers: z.array(experienceProductionBlockerSchema),
    warnings: z.array(experienceWarningSchema),
  })
  .strict();
export type ExperienceCheck = z.infer<typeof experienceCheckSchema>;
